import { createFileRoute } from '@tanstack/react-router'
import {
  DISPLAY_STATUSES,
  FraudAnalysisDatasetUploadsPage,
  fraudScoreLabelSubmissionProgressQueryOptions,
} from 'features/fraudAnalysis'
import { FraudScoreSortOrder, LabelSourceType } from 'models'
import { z } from 'zod/v4'

// These filters go to Legendary Blade as is, and it answers an empty value or a malformed date with a
// 400. Drop them here instead, so a hand-edited URL degrades to "unfiltered" rather than erroring.
// Repeated values are harmless upstream; de-duplicating them just keeps the request tidy.
const unique = <T,>(values: T[]) => [...new Set(values)]
const nonEmptyStrings = z.array(z.string().min(1)).transform(unique).optional().catch(undefined)
// Strict RFC 3339: uppercase `T`, seconds, and `Z` or a `±hh:mm` offset (`toISOString()` qualifies).
// zod alone also accepts a time without seconds, which Legendary Blade rejects.
const dateTime = z.iso
  .datetime({ offset: true })
  .regex(/T\d{2}:\d{2}:\d{2}/)
  .optional()
  .catch(undefined)

export const fraudAnalysisDatasetUploadsSearchSchema = z.object({
  sourceId: nonEmptyStrings,
  sourceType: z.array(z.enum(LabelSourceType)).transform(unique).optional().catch(undefined),
  labelSlug: nonEmptyStrings,
  status: z.array(z.enum(DISPLAY_STATUSES)).optional().catch(undefined),
  actorId: nonEmptyStrings,
  submittedAtFrom: dateTime,
  submittedAtTo: dateTime,
  sortOrder: z.enum(FraudScoreSortOrder).optional().catch(undefined),
})

export type FraudAnalysisDatasetUploadsSearchParams = z.infer<typeof fraudAnalysisDatasetUploadsSearchSchema>

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/fraud-analysis/dataset/')({
  validateSearch: fraudAnalysisDatasetUploadsSearchSchema,
  component: FraudAnalysisDatasetUploadsPage,
  loader: ({ context: { queryClient, buildRequest, fraudScoreEntitlement }, params: { subscriptionId } }) => {
    if (fraudScoreEntitlement === 'disabled') {
      return
    }

    void queryClient.prefetchInfiniteQuery(
      fraudScoreLabelSubmissionProgressQueryOptions(buildRequest, subscriptionId, { archived: false })
    )
  },
})
